import { useCallback, useMemo, useState } from 'react';

import { MarginOrderDirection, ProtectionOrderVariant } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';

import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginPositionDetails } from 'src/lib/margin/hooks/positions';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';
import { useMarginLotsError, useMarginProtectionOrderError } from 'src/lib/margin/hooks/trade';

type Props = {
  positionId?: string;
  protectionOrderType?: ProtectionOrderVariant;
};

const getProtectionOrderValue = (value?: string | null) => {
  if (!value) return '';

  if (value === '0') return '';

  return value;
};

const useMarginPositionDetailsProtectionOrderStep = ({ positionId, protectionOrderType }: Props) => {
  const {
    position,
    positionMarket,
    price: positionPrice,
    tradeType,
    side,
    currentPrice,
  } = useMarginPositionDetails({ positionId });
  const { availableMargin } = useMarginAccount();

  const { getCurrencyRate } = useMarginPrices();
  const [price, setPrice] = useState<string>(positionPrice.toString() || '');
  const [lots, setLots] = useState<string>(position?.volume.toString() || '');
  const [newStopLossPrice, setNewStopLossPrice] = useState<string>(getProtectionOrderValue(position?.stopLoss));
  const [newTakeProfitPrice, setNewTakeProfitPrice] = useState<string>(getProtectionOrderValue(position?.takeProfit));
  const [stopLossToggled, setStopLossToggled] = useState<boolean>(
    !!getProtectionOrderValue(position?.stopLoss) || protectionOrderType === ProtectionOrderVariant.StopLoss,
  );
  const [takeProfitToggled, setTakeProfitToggled] = useState<boolean>(
    !!getProtectionOrderValue(position?.takeProfit) || protectionOrderType === ProtectionOrderVariant.TakeProfit,
  );

  const audPrice = useMemo(() => {
    if (!price || !position) return Big(0).toString();

    return Big(price)
      .times(getCurrencyRate(position.sell ? 'bid' : 'ask'))
      .toString();
  }, [getCurrencyRate, position, price]);

  const maxMarginPosition = useMemo(
    () =>
      Big(audPrice).eq(0)
        ? Big(0)
        : Big(availableMargin)
            .times(positionMarket?.maxLeverage || 1)
            .div(audPrice),
    [audPrice, availableMargin, positionMarket?.maxLeverage],
  );

  const { error: lotsError } = useMarginLotsError({ market: positionMarket, lots, maxMarginPosition });

  const { error: takeProfitError } = useMarginProtectionOrderError({
    value: newTakeProfitPrice,
    tradeSide: side || MarginOrderDirection.Long,
    tradeType,
    protectionOrderType: ProtectionOrderVariant.TakeProfit,
    price: position?.opened ? currentPrice : price,
    selectedMarket: positionMarket,
  });

  const { error: stopLossError } = useMarginProtectionOrderError({
    value: newStopLossPrice,
    tradeSide: side || MarginOrderDirection.Long,
    tradeType,
    protectionOrderType: ProtectionOrderVariant.StopLoss,
    price: position?.opened ? currentPrice : price,
    selectedMarket: positionMarket,
  });

  const isValidUpdateMarginProtection = useMemo(() => {
    if (takeProfitError) return false;
    if (stopLossError) return false;
    // Only check for lotsError if the position hasnt been opened
    if (!position?.opened && lotsError !== undefined) return false;

    if (price !== positionPrice.toString()) return true;
    if (Big(lots).gt(0) && lots !== position?.volume.toString()) return true;
    if (stopLossToggled && !newStopLossPrice) return false;
    if (takeProfitToggled && !newTakeProfitPrice) return false;
    if (!stopLossToggled && !position?.stopLoss && !takeProfitToggled && !position?.takeProfit) return false;
    if (
      newTakeProfitPrice === (position?.takeProfit || '')?.toString() &&
      newStopLossPrice === (position?.stopLoss || '')?.toString()
    )
      return false;

    return true;
  }, [
    lots,
    lotsError,
    newStopLossPrice,
    newTakeProfitPrice,
    position?.stopLoss,
    position?.takeProfit,
    position?.volume,
    position?.opened,
    positionPrice,
    price,
    stopLossError,
    stopLossToggled,
    takeProfitError,
    takeProfitToggled,
  ]);

  const onStopLossToggled = useCallback(
    (checked: boolean) => {
      setStopLossToggled(checked);
      if (checked) {
        if (position?.stopLoss && Big(position.stopLoss).gt(0)) {
          setNewStopLossPrice(position?.stopLoss?.toString());
        } else {
          setNewTakeProfitPrice('');
        }
      } else {
        setNewStopLossPrice('');
      }
    },
    [position?.stopLoss],
  );

  const onTakeProfitToggled = useCallback(
    (checked: boolean) => {
      setTakeProfitToggled(checked);
      if (checked) {
        if (position?.takeProfit && Big(position.takeProfit).gt(0)) {
          setNewTakeProfitPrice(position?.takeProfit?.toString());
        } else {
          setNewTakeProfitPrice('');
        }
      } else {
        setNewTakeProfitPrice('');
      }
    },
    [position?.takeProfit],
  );

  return {
    position,
    positionMarket,
    newStopLossPrice,
    stopLossToggled,
    takeProfitToggled,
    newTakeProfitPrice,
    isValidUpdateMarginProtection,
    lots,
    price,
    audPrice,
    setNewStopLossPrice,
    setNewTakeProfitPrice,
    onStopLossToggled,
    onTakeProfitToggled,
    setLots,
    setPrice,
  };
};

export { useMarginPositionDetailsProtectionOrderStep };
